import { createContext, ReactNode, useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import { MappedStakeholder } from "src/core/constants/folder";
import { useFolder, useFolderCorporationId } from "src/core/contexts/FolderContext";
import { MappedEntity } from "src/core/types/entity";

type FolderGraphSyncContextType = {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  isCentralSelected: boolean;
  setIsCentralSelected: (selected: boolean) => void;
  openAddModal: boolean;
  setOpenAddModal: (open: boolean) => void;
  openDeleteModal: boolean;
  setOpenDeleteModal: (open: boolean) => void;
  openUpdateModal: boolean;
  setOpenUpdateModal: (open: boolean) => void;
  loadingListStakeholders: boolean;
  setLoadingListStakeholders: (loading: boolean) => void;
  handleSuccess: (createdPersonId?: string) => Promise<void>;
  selectedStakeholder: MappedStakeholder | undefined;
  setSelectedStakeholder: (stakeholder: MappedStakeholder | undefined) => void;
  selectedStakeholderForEdit: MappedStakeholder | undefined;
  setSelectedStakeholderForEdit: (stakeholder: MappedStakeholder | undefined) => void;
  pendingNavigationId: string | null;
  setPendingNavigationId: (id: string | null) => void;
  openEntityDetailsModal: boolean;
  setOpenEntityDetailsModal: (open: boolean) => void;
  selectedEntity: MappedEntity | undefined;
  setSelectedEntity: (entity: MappedEntity | undefined) => void;
  isFromBasicForm: boolean;
  setIsFromBasicForm: (isFromBasicForm: boolean) => void;
};

const FolderGraphSyncContext = createContext<FolderGraphSyncContextType | undefined>(undefined);

type FolderGraphSyncProviderProps = {
  children: ReactNode;
};

export function FolderGraphSyncProvider({ children }: FolderGraphSyncProviderProps) {
  const { refetch } = useFolder();
  const location = useLocation();
  const corporationId = useFolderCorporationId();

  // Lire le paramètre tab de l'URL
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get("tab");

  const [selectedTab, setSelectedTab] = useState<string>(tabFromUrl || "entreprise");
  const [isCentralSelected, setIsCentralSelected] = useState<boolean>(corporationId ? true : false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [loadingListStakeholders, setLoadingListStakeholders] = useState<boolean>(false);
  const [pendingNavigationId, setPendingNavigationId] = useState<string | null>(null);
  const [openEntityDetailsModal, setOpenEntityDetailsModal] = useState<boolean>(false);
  const [selectedEntity, setSelectedEntity] = useState<MappedEntity | undefined>(undefined);
  // Utilisé pour la suppression d'un stakeholder
  const [selectedStakeholder, setSelectedStakeholder] = useState<MappedStakeholder | undefined>(
    undefined
  );
  // Utilisé pour la mise à jour d'un stakeholder
  const [selectedStakeholderForEdit, setSelectedStakeholderForEdit] = useState<
    MappedStakeholder | undefined
  >(undefined);

  // Est-ce que la mise à jour d'un stakeholder est effectuée depuis le formulaire de création basique ?
  // Permet de savoir si on doit afficher les champs soft-required dans le formulaire de mise à jour.
  const [isFromBasicForm, setIsFromBasicForm] = useState<boolean>(false);

  const handleSuccess = async (createdPersonId?: string) => {
    setLoadingListStakeholders(true);
    if (createdPersonId) {
      setPendingNavigationId(createdPersonId);
    }
    await refetch();
    setLoadingListStakeholders(false);
  };

  return (
    <FolderGraphSyncContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        openAddModal,
        setOpenAddModal,
        openDeleteModal,
        setOpenDeleteModal,
        loadingListStakeholders,
        setLoadingListStakeholders,
        handleSuccess,
        selectedStakeholder,
        setSelectedStakeholder,
        selectedStakeholderForEdit,
        setSelectedStakeholderForEdit,
        pendingNavigationId,
        setPendingNavigationId,
        isCentralSelected,
        setIsCentralSelected,
        openEntityDetailsModal,
        setOpenEntityDetailsModal,
        selectedEntity,
        setSelectedEntity,
        openUpdateModal,
        setOpenUpdateModal,
        isFromBasicForm,
        setIsFromBasicForm,
      }}
    >
      {children}
    </FolderGraphSyncContext.Provider>
  );
}

export function useFolderGraphSync(): FolderGraphSyncContextType {
  const context = useContext(FolderGraphSyncContext);
  if (context === undefined) {
    throw new Error("useFolderGraphSync must be used within a FolderGraphSyncProvider");
  }
  return context;
}
